/**
 * Protein properties from sequence: molecular weight (average and monoisotopic), elemental
 * formula, composition, extinction coefficient at 280 nm, net charge and isoelectric point.
 *
 *  - Masses are summed from residue formulas using the IUPAC-style average atomic weights that
 *    Biopython also uses (C 12.0107, H 1.00794, N 14.0067, O 15.9994, S 32.065, Se 78.96) and
 *    the most abundant isotopes for monoisotopic mass. A protein is its residues plus one
 *    water. Tools that use slightly different atomic weights differ by a few hundredths of a
 *    dalton per kilodalton.
 *  - ε280 follows Pace et al. (1995, Protein Sci 4:2411): 5,500 per Trp, 1,490 per Tyr and 125
 *    per cystine (S–S) in M⁻¹ cm⁻¹, measured in 6 M guanidinium chloride.
 *  - pI is where the Henderson–Hasselbalch net charge is zero, for a stated set of pKa values.
 */

export type Element = "C" | "H" | "N" | "O" | "S" | "Se";
export type Formula = Partial<Record<Element, number>>;

export const ATOMIC_MASS: Readonly<
  Record<Element, { average: number; monoisotopic: number }>
> = {
  C: { average: 12.0107, monoisotopic: 12 },
  H: { average: 1.007_94, monoisotopic: 1.007_825_032_07 },
  N: { average: 14.0067, monoisotopic: 14.003_074_004_8 },
  O: { average: 15.9994, monoisotopic: 15.994_914_619_56 },
  S: { average: 32.065, monoisotopic: 31.972_071 },
  Se: { average: 78.96, monoisotopic: 79.916_521_8 },
};

/** Proton mass, for [M+H]⁺ ions (CODATA). */
export const PROTON_MASS = 1.007_276_466_621;

export interface Residue {
  one: string;
  three: string;
  /** Formula of the residue as it sits in a chain (amino acid minus water). */
  formula: Formula;
}

export const RESIDUES: readonly Residue[] = [
  { one: "A", three: "Ala", formula: { C: 3, H: 5, N: 1, O: 1 } },
  { one: "R", three: "Arg", formula: { C: 6, H: 12, N: 4, O: 1 } },
  { one: "N", three: "Asn", formula: { C: 4, H: 6, N: 2, O: 2 } },
  { one: "D", three: "Asp", formula: { C: 4, H: 5, N: 1, O: 3 } },
  { one: "C", three: "Cys", formula: { C: 3, H: 5, N: 1, O: 1, S: 1 } },
  { one: "Q", three: "Gln", formula: { C: 5, H: 8, N: 2, O: 2 } },
  { one: "E", three: "Glu", formula: { C: 5, H: 7, N: 1, O: 3 } },
  { one: "G", three: "Gly", formula: { C: 2, H: 3, N: 1, O: 1 } },
  { one: "H", three: "His", formula: { C: 6, H: 7, N: 3, O: 1 } },
  { one: "I", three: "Ile", formula: { C: 6, H: 11, N: 1, O: 1 } },
  { one: "L", three: "Leu", formula: { C: 6, H: 11, N: 1, O: 1 } },
  { one: "K", three: "Lys", formula: { C: 6, H: 12, N: 2, O: 1 } },
  { one: "M", three: "Met", formula: { C: 5, H: 9, N: 1, O: 1, S: 1 } },
  { one: "F", three: "Phe", formula: { C: 9, H: 9, N: 1, O: 1 } },
  { one: "P", three: "Pro", formula: { C: 5, H: 7, N: 1, O: 1 } },
  { one: "S", three: "Ser", formula: { C: 3, H: 5, N: 1, O: 2 } },
  { one: "T", three: "Thr", formula: { C: 4, H: 7, N: 1, O: 2 } },
  { one: "W", three: "Trp", formula: { C: 11, H: 10, N: 2, O: 1 } },
  { one: "Y", three: "Tyr", formula: { C: 9, H: 9, N: 1, O: 2 } },
  { one: "V", three: "Val", formula: { C: 5, H: 9, N: 1, O: 1 } },
  { one: "U", three: "Sec", formula: { C: 3, H: 5, N: 1, O: 1, Se: 1 } },
  { one: "O", three: "Pyl", formula: { C: 12, H: 19, N: 3, O: 2 } },
];

const RESIDUE_BY_LETTER = new Map(
  RESIDUES.map((residue) => [residue.one, residue])
);
const AMBIGUOUS = new Set(["B", "J", "X", "Z"]);

export type Composition = Record<string, number>;

export interface ParsedProtein {
  composition: Composition;
  /** Residues counted. */
  length: number;
  /** First and last recognised residue, for terminus-specific pKa values. */
  first: string;
  last: string;
  /** Count of B, J, X and Z, which have no single mass and are left out. */
  ambiguous: number;
  /** Distinct characters that were not letters and were dropped. */
  dropped: string[];
}

const SPACE_AND_DIGITS = /[\s\d]+/g;

/** Read a protein sequence: standard residues plus U (Sec) and O (Pyl). */
export function parseProtein(raw: string): ParsedProtein {
  const composition: Composition = {};
  const dropped = new Set<string>();
  let length = 0;
  let ambiguous = 0;
  let first = "";
  let last = "";
  for (const character of raw.replace(SPACE_AND_DIGITS, "").toUpperCase()) {
    if (RESIDUE_BY_LETTER.has(character)) {
      composition[character] = (composition[character] ?? 0) + 1;
      length += 1;
      first = first || character;
      last = character;
    } else if (AMBIGUOUS.has(character)) {
      ambiguous += 1;
    } else if (character !== "*") {
      dropped.add(character);
    }
  }
  return { composition, length, first, last, ambiguous, dropped: [...dropped] };
}

export interface Modifications {
  /** Acetylated N-terminus (adds C₂H₂O; removes the N-terminal charge). */
  acetylation: boolean;
  /** Amidated C-terminus (COOH becomes CONH₂; removes the C-terminal charge). */
  amidation: boolean;
  /** Number of disulfide bonds (each removes two H and two cysteine thiols). */
  disulfides: number;
}

export const NO_MODIFICATIONS: Modifications = {
  acetylation: false,
  amidation: false,
  disulfides: 0,
};

const WATER: Formula = { H: 2, O: 1 };

function addInto(target: Formula, add: Formula, times: number): void {
  for (const [element, count] of Object.entries(add) as [Element, number][]) {
    target[element] = (target[element] ?? 0) + count * times;
  }
}

/** Elemental formula of the whole molecule: residues + H₂O, adjusted for modifications. */
export function formulaOf(
  composition: Composition,
  mods: Modifications = NO_MODIFICATIONS
): Formula {
  const total: Formula = {};
  for (const [letter, count] of Object.entries(composition)) {
    const residue = RESIDUE_BY_LETTER.get(letter);
    if (residue) {
      addInto(total, residue.formula, count);
    }
  }
  addInto(total, WATER, 1);
  if (mods.acetylation) {
    addInto(total, { C: 2, H: 2, O: 1 }, 1);
  }
  if (mods.amidation) {
    addInto(total, { O: -1, N: 1, H: 1 }, 1);
  }
  addInto(total, { H: -2 }, mods.disulfides);
  return total;
}

export function massOf(
  formula: Formula,
  kind: "average" | "monoisotopic"
): number {
  let mass = 0;
  for (const [element, count] of Object.entries(formula) as [
    Element,
    number,
  ][]) {
    mass += ATOMIC_MASS[element][kind] * count;
  }
  return mass;
}

/** Hill-style formula text: C, H first, then the rest alphabetically. */
export function formulaText(formula: Formula): string {
  const order: Element[] = ["C", "H", "N", "O", "S", "Se"];
  return order
    .filter((element) => (formula[element] ?? 0) > 0)
    .map(
      (element) => `${element}${formula[element] === 1 ? "" : formula[element]}`
    )
    .join("");
}

export interface Extinction {
  /** All Cys reduced (no cystine contribution). */
  reduced: number;
  /** Cys residues paired into cystines. */
  withCystines: number;
  cystines: number;
}

const EPSILON_TRP = 5500;
const EPSILON_TYR = 1490;
const EPSILON_CYSTINE = 125;

/**
 * Pace et al. (1995) extinction coefficient at 280 nm in M⁻¹ cm⁻¹. With `disulfides` given,
 * that many cystines are counted; otherwise every pair of Cys is assumed to form one, as
 * ExPASy ProtParam reports.
 */
export function extinction(
  composition: Composition,
  disulfides?: number
): Extinction {
  const trp = composition.W ?? 0;
  const tyr = composition.Y ?? 0;
  const cys = composition.C ?? 0;
  const cystines = Math.min(
    disulfides ?? Math.floor(cys / 2),
    Math.floor(cys / 2)
  );
  const reduced = trp * EPSILON_TRP + tyr * EPSILON_TYR;
  return {
    reduced,
    withCystines: reduced + cystines * EPSILON_CYSTINE,
    cystines,
  };
}

export interface PkaSet {
  id: "bjellqvist" | "emboss";
  nTerm: number;
  cTerm: number;
  /** Replacement N-terminal pK for particular first residues. */
  nTermByResidue: Readonly<Record<string, number>>;
  /** Replacement C-terminal pK for particular last residues. */
  cTermByResidue: Readonly<Record<string, number>>;
  K: number;
  R: number;
  H: number;
  D: number;
  E: number;
  C: number;
  Y: number;
}

/** Bjellqvist et al. (1993, 1994), the values behind ExPASy Compute pI/Mw and Biopython. */
export const BJELLQVIST: PkaSet = {
  id: "bjellqvist",
  nTerm: 7.5,
  cTerm: 3.55,
  nTermByResidue: {
    A: 7.59,
    M: 7.0,
    S: 6.93,
    P: 8.36,
    T: 6.82,
    V: 7.44,
    E: 7.7,
  },
  cTermByResidue: { D: 4.55, E: 4.75 },
  K: 10.0,
  R: 12.0,
  H: 5.98,
  D: 4.05,
  E: 4.45,
  C: 9.0,
  Y: 10.0,
};

/** The pK values of EMBOSS iep (Rice, Longden & Bleasby 2000), as tabulated by BioPerl and IPC. */
export const EMBOSS: PkaSet = {
  id: "emboss",
  nTerm: 8.6,
  cTerm: 3.6,
  nTermByResidue: {},
  cTermByResidue: {},
  K: 10.8,
  R: 12.5,
  H: 6.5,
  D: 3.9,
  E: 4.1,
  C: 8.5,
  Y: 10.1,
};

export const PKA_SETS: Readonly<Record<PkaSet["id"], PkaSet>> = {
  bjellqvist: BJELLQVIST,
  emboss: EMBOSS,
};

export interface ChargeInput {
  composition: Composition;
  first: string;
  last: string;
  mods: Modifications;
  pka: PkaSet;
}

function positiveFraction(pH: number, pK: number): number {
  return 1 / (10 ** (pH - pK) + 1);
}

function negativeFraction(pH: number, pK: number): number {
  return 1 / (10 ** (pK - pH) + 1);
}

/** Net charge at `pH` by the Henderson–Hasselbalch equation. */
export function netCharge(input: ChargeInput, pH: number): number {
  const { composition, pka, mods } = input;
  const count = (letter: string) => composition[letter] ?? 0;
  let charge = 0;
  if (!mods.acetylation) {
    charge += positiveFraction(
      pH,
      pka.nTermByResidue[input.first] ?? pka.nTerm
    );
  }
  charge += count("K") * positiveFraction(pH, pka.K);
  charge += count("R") * positiveFraction(pH, pka.R);
  charge += count("H") * positiveFraction(pH, pka.H);
  if (!mods.amidation) {
    charge -= negativeFraction(pH, pka.cTermByResidue[input.last] ?? pka.cTerm);
  }
  charge -= count("D") * negativeFraction(pH, pka.D);
  charge -= count("E") * negativeFraction(pH, pka.E);
  charge -=
    Math.max(0, count("C") - 2 * mods.disulfides) * negativeFraction(pH, pka.C);
  charge -= count("Y") * negativeFraction(pH, pka.Y);
  return charge;
}

/** The pH where the net charge is zero, by bisection between pH 0 and 14. */
export function isoelectricPoint(input: ChargeInput): number {
  let low = 0;
  let high = 14;
  for (let step = 0; step < 60; step++) {
    const middle = (low + high) / 2;
    if (netCharge(input, middle) > 0) {
      low = middle;
    } else {
      high = middle;
    }
  }
  return (low + high) / 2;
}

/** Settings a protein calculator passes to `analyseProtein`. */
export interface AnalysisSettings {
  acetylation: boolean;
  amidation: boolean;
  bonds: number | null;
  pkaId: PkaSet["id"];
}

export function analyseProtein(
  parsed: ParsedProtein,
  settings: AnalysisSettings
) {
  const allowed = Math.floor((parsed.composition.C ?? 0) / 2);
  const requested =
    settings.bonds !== null && settings.bonds >= 0 ? settings.bonds : 0;
  const used = Math.min(requested, allowed);
  const mods: Modifications = {
    acetylation: settings.acetylation,
    amidation: settings.amidation,
    disulfides: used,
  };
  const formula = formulaOf(parsed.composition, mods);
  const charge = {
    composition: parsed.composition,
    first: parsed.first,
    last: parsed.last,
    mods,
    pka: PKA_SETS[settings.pkaId],
  };
  return {
    used,
    capped: used < requested,
    formula,
    average: massOf(formula, "average"),
    mono: massOf(formula, "monoisotopic"),
    pi: isoelectricPoint(charge),
    charge,
    epsilon: extinction(parsed.composition, used > 0 ? used : undefined),
    assumedPairs: used === 0,
  };
}

export type Analysis = ReturnType<typeof analyseProtein>;
